import { PendingChatImage, SentChatImage } from "@/core/components/chat-images";
import { ChatMarkdown } from "@/core/components/chat-markdown";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { getChatMessageSpacing } from "./chat-message-spacing";
import { CollapsibleSection } from "./collapsible-section";
import { MessageSources } from "./message-sources";
import { SelectionContextChip } from "./selection-context-chip";
import type { ChatMessage } from "./types";

interface ChatBubbleProps {
  message: ChatMessage;
  prevRole?: ChatMessage["role"];
  /** Where a saved screenshot is read, on the chats that take them. */
  imagePath?: (imageId: string) => string;
}

export const ChatBubble = ({
  message,
  prevRole,
  imagePath,
}: ChatBubbleProps) => {
  const spacingClasses = getChatMessageSpacing(message.role, prevRole);

  if (message.role === "user") {
    return (
      <div
        className={cn("flex w-full flex-col items-end gap-1.5", spacingClasses)}
      >
        {message.selectionContext && (
          <span className="max-w-[85%]">
            <SelectionContextChip context={message.selectionContext} />
          </span>
        )}
        {((imagePath && message.images?.length) ||
          !!message.localImages?.length) && (
          <Stack
            direction="row"
            justify="end"
            gap={2}
            className="max-w-[85%] flex-wrap"
          >
            {message.localImages?.map((image, index) => (
              <PendingChatImage key={`${image.name}-${index}`} image={image} />
            ))}
            {imagePath &&
              message.images?.map((image) => (
                <SentChatImage
                  key={image.id}
                  path={imagePath(image.id)}
                  image={image}
                />
              ))}
          </Stack>
        )}
        <div className="max-w-[85%] rounded-2xl bg-neutral px-3 py-2 text-sm wrap-break-word whitespace-pre-wrap text-ink">
          {message.content}
        </div>
      </div>
    );
  }

  if (message.error) {
    return (
      <div
        role="alert"
        className={cn(
          "w-full rounded-xl border border-danger-line bg-danger-subtle px-3 py-2 text-sm wrap-break-word text-danger",
          spacingClasses,
        )}
      >
        {message.content}
      </div>
    );
  }

  return (
    <div className={cn("w-full min-w-0", spacingClasses)}>
      <ChatMarkdown>{message.content}</ChatMarkdown>

      {message.steps && message.steps.length > 0 && (
        <CollapsibleSection label={`What I checked (${message.steps.length})`}>
          <Stack align="start" gap={1}>
            {message.steps.map((step, index) => (
              <p key={`${step}-${index}`} className="text-xs text-subtle">
                {step}
              </p>
            ))}
          </Stack>
        </CollapsibleSection>
      )}

      {message.sources && message.sources.length > 0 && (
        <MessageSources sources={message.sources} />
      )}
    </div>
  );
};
